import React, { useRef, useState } from 'react';
import {
    AlertTriangle, ArrowLeft, CheckCircle2, FileText, Image as ImageIcon, Loader2, Mail, MapPin, MapPinOff, Paperclip, Phone, Save,
    ShieldCheck, Trash2,
} from 'lucide-react';
import { Button } from '../Button';
import { describeError } from '../../services/errorMessage';
import { istNativeApp } from '../../services/nativePlatform';
import { dateiLink } from '../../services/talentpool/talentpoolService';
import {
    STATUS_LABEL,
    arbeitsformLabel,
    dateienFehlen,
    datumDe,
    entfernungText,
    erfahrungText,
    groesseText,
    hatAenderung,
    hatStandort,
    lesbareAngaben,
    loeschFaellig,
    loeschfristText,
    mailHref,
    ortText,
    telHref,
    umkreisText,
    zeitpunktDe,
    type TalentEintrag,
} from '../../services/talentpool/talentpoolLogik';
import { TALENT_GRENZEN, TALENT_STATUS, type TalentDatei, type TalentStatus, type TalentpoolZeile } from '../../services/talentpool/talentpoolTypes';
import { BundesweitChip, RolleChip, StatusChip } from './TalentpoolChips';
import { TalentLoeschenDialog } from './TalentLoeschenDialog';

/**
 * Eine Bewerbung mit allen Angaben (Seitenleiste neben der Karte): Kontakt
 * als mailto/tel, Standort und Umkreis, Qualifikation, Zusatzangaben der
 * Website, Anhänge (kurz gültiger Link), Einwilligungen und Löschfrist.
 * Darunter Status und Notiz (RPC `talentpool_bearbeiten`) und Löschen mit
 * eigener Rückfrage.
 *
 * Der Aufrufer hängt die Ansicht mit `key={zeile.id}` ein: eine andere
 * Bewerbung beginnt mit frischem Bearbeitungsstand.
 */

interface Props {
    zeile: TalentpoolZeile;
    /** YYYY-MM-DD, Bezug für die Löschfrist. */
    heute: string;
    /** Entfernung zum Suchpunkt, falls einer gesetzt ist. */
    suchEintrag: TalentEintrag | null;
    onZurueck: () => void;
    onSpeichern: (status: TalentStatus, notiz: string) => Promise<unknown>;
    onLoeschen: () => Promise<unknown>;
}

const Abschnitt: React.FC<{ titel: string; children: React.ReactNode }> = ({ titel, children }) => (
    <section className="space-y-1.5">
        <h4 className="text-[11px] font-bold text-slate-400 uppercase">{titel}</h4>
        {children}
    </section>
);

const Zeile: React.FC<{ titel: string; children: React.ReactNode }> = ({ titel, children }) => (
    <>
        <dt className="text-xs text-slate-400 pt-0.5">{titel}</dt>
        <dd className="text-sm text-slate-700 break-words min-w-0">{children}</dd>
    </>
);

const DL = 'grid grid-cols-[minmax(6.5rem,auto)_1fr] gap-x-3 gap-y-1.5';

const DateiIcon: React.FC<{ typ: string }> = ({ typ }) =>
    typ.startsWith('image/') ? <ImageIcon className="w-4 h-4 shrink-0 text-slate-400" /> : <FileText className="w-4 h-4 shrink-0 text-slate-400" />;

export const TalentpoolDetail: React.FC<Props> = ({ zeile, heute, suchEintrag, onZurueck, onSpeichern, onLoeschen }) => {
    const [status, setStatus] = useState<TalentStatus>(zeile.status);
    const [notiz, setNotiz] = useState(zeile.notiz ?? '');
    const [speichert, setSpeichert] = useState(false);
    const [speicherFehler, setSpeicherFehler] = useState('');
    const [gespeichert, setGespeichert] = useState(false);
    const speichertRef = useRef(false);
    const [loeschenOffen, setLoeschenOffen] = useState(false);
    const [oeffnet, setOeffnet] = useState<string | null>(null);
    const [dateiFehler, setDateiFehler] = useState('');

    const geaendert = hatAenderung(zeile, status, notiz);
    const faellig = loeschFaellig(zeile, heute);
    const fehlen = dateienFehlen(zeile);
    const angaben = lesbareAngaben(zeile.angaben);
    const mail = mailHref(zeile.email);
    const tel = telHref(zeile.telefon);
    const ort = ortText(zeile);

    const speichern = async () => {
        if (speichertRef.current || !geaendert) return;
        speichertRef.current = true;
        setSpeichert(true);
        setSpeicherFehler('');
        setGespeichert(false);
        try {
            await onSpeichern(status, notiz);
            setGespeichert(true);
        } catch (e) {
            setSpeicherFehler(describeError(e));
        } finally {
            speichertRef.current = false;
            setSpeichert(false);
        }
    };

    // Anhang öffnen: Tab SOFORT im Klick, erst nach dem Signieren umlenken
    // (Muster CustomerDetail) — ein Öffnen nach dem await hätte keine
    // Benutzeraktivierung mehr und fiele dem Popup-Blocker zum Opfer.
    const oeffneDatei = async (d: TalentDatei) => {
        setDateiFehler('');
        const tab = istNativeApp() ? null : window.open('', '_blank');
        if (!istNativeApp() && !tab) {
            setDateiFehler('Der Browser hat den neuen Tab blockiert. Bitte Pop-ups für diese Seite erlauben.');
            return;
        }
        if (tab) {
            tab.opener = null;
            tab.document.title = 'Anhang wird geladen…';
        }
        setOeffnet(d.pfad);
        try {
            const url = await dateiLink(d.pfad);
            if (tab) tab.location.href = url;
            else window.open(url, '_blank', 'noopener');
        } catch (e) {
            tab?.close();
            setDateiFehler(`„${d.name}“ konnte nicht geöffnet werden: ${describeError(e)}`);
        } finally {
            setOeffnet(null);
        }
    };

    return (
        <section className="space-y-4" aria-labelledby="talent-detail-titel">
            <button
                type="button"
                onClick={onZurueck}
                className="min-h-[36px] inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-800"
            >
                <ArrowLeft className="w-4 h-4" /> Zur Liste
            </button>

            <div data-glass="karte" className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm space-y-4">
                {/* ── Kopf ── */}
                <div className="space-y-1.5">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                        <h3 id="talent-detail-titel" className="text-lg font-bold text-slate-800 break-words min-w-0">{zeile.name}</h3>
                        <StatusChip status={zeile.status} />
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                        <RolleChip rolle={zeile.rolle} lang />
                        {zeile.bundesweit && <BundesweitChip />}
                    </div>
                    <p className="text-xs text-slate-400">Eingang {zeitpunktDe(zeile.eingegangen_am)}</p>
                </div>

                {/* ── Kontakt ── */}
                <Abschnitt titel="Kontakt">
                    <div className="flex flex-col gap-1">
                        {zeile.email && (
                            mail
                                ? <a href={mail} className="inline-flex items-center gap-2 text-sm text-indigo-700 hover:underline break-all"><Mail className="w-4 h-4 shrink-0" />{zeile.email}</a>
                                : <span className="inline-flex items-center gap-2 text-sm text-slate-700 break-all"><Mail className="w-4 h-4 shrink-0 text-slate-400" />{zeile.email}</span>
                        )}
                        {zeile.telefon && (
                            tel
                                ? <a href={tel} className="inline-flex items-center gap-2 text-sm text-indigo-700 hover:underline"><Phone className="w-4 h-4 shrink-0" />{zeile.telefon}</a>
                                : <span className="inline-flex items-center gap-2 text-sm text-slate-700"><Phone className="w-4 h-4 shrink-0 text-slate-400" />{zeile.telefon}</span>
                        )}
                        {!zeile.email && !zeile.telefon && <p className="text-sm text-slate-400">Keine Kontaktangabe.</p>}
                    </div>
                </Abschnitt>

                {/* ── Standort ── */}
                <Abschnitt titel="Standort">
                    <dl className={DL}>
                        <Zeile titel="Ort">
                            {hatStandort(zeile)
                                ? <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-slate-400" />{ort || 'auf der Karte'}</span>
                                : <span className="inline-flex items-center gap-1 font-semibold text-amber-700"><MapPinOff className="w-3.5 h-3.5" />ohne Standort{ort ? ` (${ort})` : ''}</span>}
                        </Zeile>
                        <Zeile titel="Umkreis">{umkreisText(zeile)}</Zeile>
                        {suchEintrag && (
                            <Zeile titel="Suchpunkt">
                                {suchEintrag.km === null ? 'keine Entfernung (ohne Standort)' : entfernungText(suchEintrag.km)}
                                {' · '}
                                {suchEintrag.erreicht === true ? <span className="font-semibold text-emerald-700">erreicht</span>
                                    : suchEintrag.erreicht === false ? <span className="text-slate-500">zu weit</span>
                                        : <span className="text-amber-700">unklar</span>}
                            </Zeile>
                        )}
                    </dl>
                </Abschnitt>

                {/* ── Profil ── */}
                <Abschnitt titel="Profil">
                    <dl className={DL}>
                        {zeile.arbeitsform.length > 0 && <Zeile titel="Arbeitsform">{zeile.arbeitsform.map(arbeitsformLabel).join(', ')}</Zeile>}
                        {zeile.qualifikation.length > 0 && (
                            <Zeile titel="Qualifikation">
                                <ul className="list-disc pl-4 space-y-0.5">
                                    {zeile.qualifikation.map(q => <li key={q}>{q}</li>)}
                                </ul>
                            </Zeile>
                        )}
                        {zeile.qualifikation_sonstiges && <Zeile titel="Sonstige Qualifikation">{zeile.qualifikation_sonstiges}</Zeile>}
                        {zeile.abschluss && <Zeile titel="Berufsabschluss">{zeile.abschluss}</Zeile>}
                        {zeile.erfahrung_jahre !== null && <Zeile titel="Berufserfahrung">{erfahrungText(zeile.erfahrung_jahre)}</Zeile>}
                        {zeile.verfuegbar_ab && <Zeile titel="Verfügbar ab">{datumDe(zeile.verfuegbar_ab)}</Zeile>}
                        {angaben.map(a => <Zeile key={a.schluessel} titel={a.titel}>{a.wert}</Zeile>)}
                    </dl>
                </Abschnitt>

                {zeile.nachricht && (
                    <Abschnitt titel="Nachricht">
                        <p className="text-sm text-slate-700 whitespace-pre-wrap break-words bg-slate-50 border border-slate-100 rounded-xl p-3">{zeile.nachricht}</p>
                    </Abschnitt>
                )}

                {/* ── Herkunft ── */}
                {(zeile.herkunft || zeile.kampagne || zeile.quelle_seite) && (
                    <Abschnitt titel="Herkunft">
                        <dl className={DL}>
                            {zeile.herkunft && <Zeile titel="Aufmerksam durch">{zeile.herkunft}</Zeile>}
                            {zeile.kampagne && <Zeile titel="Kampagne">{zeile.kampagne}</Zeile>}
                            {zeile.quelle_seite && <Zeile titel="Seite">{zeile.quelle_seite}</Zeile>}
                        </dl>
                    </Abschnitt>
                )}

                {/* ── Anhänge ── */}
                {(zeile.dateien.length > 0 || fehlen > 0) && (
                    <Abschnitt titel="Anhänge">
                        <ul className="space-y-1.5">
                            {zeile.dateien.map(d => (
                                <li key={d.pfad}>
                                    <button
                                        type="button"
                                        onClick={() => { void oeffneDatei(d); }}
                                        disabled={oeffnet !== null}
                                        className="w-full min-h-[40px] flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-left text-sm text-slate-700 hover:bg-white hover:border-slate-300 disabled:opacity-60"
                                    >
                                        {oeffnet === d.pfad ? <Loader2 className="w-4 h-4 shrink-0 animate-spin text-indigo-600" /> : <DateiIcon typ={d.typ} />}
                                        <span className="truncate flex-1">{d.name}</span>
                                        <span className="text-[11px] text-slate-400 tabular-nums shrink-0">{groesseText(d.groesse)}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                        {fehlen > 0 && (
                            <p className="flex items-start gap-1.5 text-xs text-amber-800">
                                <Paperclip className="w-3.5 h-3.5 shrink-0 mt-px" />
                                {fehlen === 1 ? 'Ein angekündigter Anhang fehlt' : `${fehlen} angekündigte Anhänge fehlen`}. Meist liefert die Website binnen zwei Stunden nach, sonst beim Bewerber anfordern.
                            </p>
                        )}
                        {dateiFehler && <p role="alert" className="text-xs text-red-700">{dateiFehler}</p>}
                    </Abschnitt>
                )}

                {/* ── Datenschutz ── */}
                <Abschnitt titel="Datenschutz">
                    <dl className={DL}>
                        <Zeile titel="Einwilligung">{zeile.einwilligung_datenschutz ? 'Datenschutz erteilt' : 'Datenschutz nicht erteilt'}</Zeile>
                        <Zeile titel="Talentpool">{zeile.einwilligung_talentpool ? 'bis zu zwei Jahre speichern: ja' : 'nur für dieses Verfahren'}</Zeile>
                        <Zeile titel="Löschfrist">
                            <span className={faellig ? 'inline-flex items-start gap-1 font-semibold text-red-700' : ''}>
                                {faellig && <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />}
                                {loeschfristText(zeile, heute)}
                            </span>
                        </Zeile>
                        {zeile.abgeschlossen_am && <Zeile titel="Abgeschlossen">{zeitpunktDe(zeile.abgeschlossen_am)}</Zeile>}
                    </dl>
                </Abschnitt>
            </div>

            {/* ── Bearbeiten ── */}
            <div data-glass="karte" className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm space-y-3">
                <h4 className="text-[11px] font-bold text-slate-400 uppercase">Bearbeitung</h4>
                <label className="block space-y-1">
                    <span className="text-xs font-semibold text-slate-600">Status</span>
                    <select
                        value={status}
                        onChange={(e) => { setStatus(e.target.value as TalentStatus); setGespeichert(false); }}
                        className="w-full min-h-[40px] px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none"
                    >
                        {TALENT_STATUS.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                    </select>
                </label>
                <label className="block space-y-1">
                    <span className="text-xs font-semibold text-slate-600">Notiz (nur intern)</span>
                    <textarea
                        value={notiz}
                        onChange={(e) => { setNotiz(e.target.value); setGespeichert(false); }}
                        maxLength={TALENT_GRENZEN.notiz}
                        rows={4}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none resize-y"
                    />
                </label>
                {(status === 'zusage' || status === 'absage') && zeile.status !== status && !zeile.einwilligung_talentpool && (
                    <p className="text-xs text-slate-500">Mit {STATUS_LABEL[status]} beginnt die Löschfrist von sechs Monaten.</p>
                )}
                <div className="flex flex-wrap items-center gap-2">
                    <Button icon={<Save className="w-4 h-4" />} loading={speichert} disabled={!geaendert} onClick={() => { void speichern(); }} className="min-h-[44px]">
                        Speichern
                    </Button>
                    {gespeichert && !geaendert && (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-700" aria-live="polite">
                            <CheckCircle2 className="w-4 h-4" /> Gespeichert
                        </span>
                    )}
                    {zeile.bearbeitet_am && (
                        <span className="text-[11px] text-slate-400 ml-auto">Zuletzt bearbeitet {zeitpunktDe(zeile.bearbeitet_am)}</span>
                    )}
                </div>
                {speicherFehler && <p role="alert" className="text-xs text-red-700">{speicherFehler}</p>}
            </div>

            <div className={`flex flex-wrap items-center justify-between gap-2 rounded-2xl p-3 border ${faellig ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
                <p className="text-xs text-slate-600 flex items-center gap-1.5">
                    <ShieldCheck className="w-4 h-4 text-slate-400 shrink-0" />
                    {faellig ? 'Löschfrist erreicht — bitte löschen.' : 'Löschen entfernt Angaben und Anhänge endgültig.'}
                </p>
                <Button variant={faellig ? 'danger' : 'outline'} size="sm" icon={<Trash2 className="w-3.5 h-3.5" />} onClick={() => setLoeschenOffen(true)}>
                    Löschen
                </Button>
            </div>

            {loeschenOffen && (
                <TalentLoeschenDialog
                    name={zeile.name}
                    anhaenge={zeile.dateien.length}
                    loeschFaellig={faellig}
                    onLoeschen={onLoeschen}
                    onAbbrechen={() => setLoeschenOffen(false)}
                />
            )}
        </section>
    );
};
